import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Fade,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Clear as ClearIcon,
  DarkMode as DarkModeIcon,
  ErrorOutline as ErrorOutlineIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  LightMode as LightModeIcon,
  LocalShipping as TruckIcon,
  LinkOff as LinkOffIcon,
  MyLocation as MyLocationIcon,
  Route as RouteIcon,
  RvHookup as TrailerIcon,
  Search as SearchIcon,
  Speed as SpeedIcon,
  ZoomOutMap as ZoomOutMapIcon,
} from "@mui/icons-material";
import { GoogleMap, Libraries, useLoadScript } from "@react-google-maps/api";
import {
  PublicAssetLocation,
  ShareAssetType,
  useGetPublicTrackingQuery,
  useGetPublicVehicleHistoryQuery,
} from "../../api/shareApi";
import { getGoogleMapsApiKey } from "../../config/googleMaps";
import { createTrailerMarkerElement } from "../dashboard/hooks/map/useAnyTrackMarkers";
import { createVehicleIconElement } from "../dashboard/utils/markerUtils";
import {
  createHistoryPolyline,
  createPauseMarker,
  createStartMarker,
  processVehicleHistory,
} from "../dashboard/utils/vehicleHistoryUtils";
import { useTheme as useAppTheme } from "../../theme/ThemeContext";

const POLLING_INTERVAL_MS = 10_000;
const GOOGLE_MAPS_LIBRARIES: Libraries = ["marker", "core"];
const mapContainerStyle = { width: "100%", height: "100%" };
const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 };
const DEFAULT_ZOOM = 5;
const ASSET_ZOOM = 16;

const getAssetKey = (asset: PublicAssetLocation) =>
  asset.assetType === ShareAssetType.Vehicle
    ? `vehicle-${asset.vehicleId}`
    : `trailer-${asset.deviceId}`;

const hasPosition = (asset: PublicAssetLocation) =>
  asset.latitude != null && asset.longitude != null;

const getLastUpdate = (asset: PublicAssetLocation) =>
  asset.lastUpdate || asset.locationTime || null;

const matchesSearch = (asset: PublicAssetLocation, query: string) => {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    asset.name.toLowerCase().includes(q) ||
    (asset.licensePlateNumber?.toLowerCase().includes(q) ?? false) ||
    (asset.deviceId?.toLowerCase().includes(q) ?? false)
  );
};

const createPublicVehicleMarkerElement = (
  asset: PublicAssetLocation,
  isDarkMode: boolean,
  isSelected: boolean
): HTMLElement => {
  const markerElement = document.createElement("div");
  markerElement.setAttribute("aria-label", asset.name);
  markerElement.style.position = "relative";
  markerElement.style.width = "32px";
  markerElement.style.height = "32px";
  markerElement.style.display = "flex";
  markerElement.style.alignItems = "center";
  markerElement.style.justifyContent = "center";
  markerElement.style.pointerEvents = "none";
  markerElement.style.filter = "drop-shadow(0 2px 5px rgba(0,0,0,0.45))";

  const label = document.createElement("div");
  label.textContent = asset.name;
  label.style.position = "absolute";
  label.style.bottom = "100%";
  label.style.left = "50%";
  label.style.transform = "translateX(-50%)";
  label.style.marginBottom = "4px";
  label.style.backgroundColor = isDarkMode
    ? "rgba(33, 33, 33, 0.9)"
    : "rgba(255, 255, 255, 0.9)";
  label.style.color = isDarkMode ? "#fff" : "#000";
  label.style.padding = "2px 4px";
  label.style.borderRadius = "4px";
  label.style.fontSize = "10px";
  label.style.border = isDarkMode
    ? "1px solid rgba(255,255,255,0.2)"
    : "1px solid rgba(0,0,0,0.1)";
  label.style.whiteSpace = "nowrap";
  label.style.zIndex = "1";
  label.style.maxWidth = "80px";
  label.style.overflow = "hidden";
  label.style.textOverflow = "ellipsis";
  label.style.fontFamily = "Roboto, sans-serif";
  label.style.fontWeight = "500";
  label.style.boxShadow = "0 2px 4px rgba(0,0,0,0.2)";

  const iconEl = createVehicleIconElement({
    speed: asset.speed,
    heading: asset.heading,
    isSelected,
    isDarkMode,
  });
  iconEl.style.transform = "scale(1.45)";
  iconEl.style.transformOrigin = "center";

  markerElement.appendChild(label);
  markerElement.appendChild(iconEl);
  return markerElement;
};

const AssetList: React.FC<{
  assets: PublicAssetLocation[];
  selectedKey: string | undefined;
  onSelect: (asset: PublicAssetLocation) => void;
  emptyLabel: string;
}> = ({ assets, selectedKey, onSelect, emptyLabel }) => {
  if (assets.length === 0) {
    return (
      <Typography variant="caption" color="text.disabled" sx={{ px: 1, py: 0.5, display: "block" }}>
        {emptyLabel}
      </Typography>
    );
  }

  return (
    <List dense disablePadding>
      {assets.map((asset) => {
        const key = getAssetKey(asset);
        const lastUpdate = getLastUpdate(asset);
        return (
          <ListItemButton
            key={key}
            selected={selectedKey === key}
            onClick={() => onSelect(asset)}
            sx={{ borderRadius: 1, mb: 0.25, px: 1, py: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              {asset.assetType === ShareAssetType.Vehicle
                ? <TruckIcon fontSize="small" />
                : <TrailerIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="body2" noWrap fontWeight={selectedKey === key ? 600 : 400}>
                  {asset.name}
                </Typography>
              }
              secondary={
                <Typography variant="caption" noWrap color="text.secondary">
                  {lastUpdate
                    ? new Date(lastUpdate).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                    : "No update"}
                </Typography>
              }
            />
          </ListItemButton>
        );
      })}
    </List>
  );
};

const PublicTrackingPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const muiTheme = useTheme();
  const { mode, toggleTheme } = useAppTheme();
  const isDark = muiTheme.palette.mode === "dark";

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [mapKey, setMapKey] = useState(0);
  const [showRoute, setShowRoute] = useState(false);
  const [selectedAssetKey, setSelectedAssetKey] = useState<string | null>(null);
  const [routeVehicleId, setRouteVehicleId] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [isPanelExpanded, setIsPanelExpanded] = useState(true);

  const markersRef = useRef<Map<string, google.maps.marker.AdvancedMarkerElement>>(new Map());
  const routePolylineRef = useRef<google.maps.Polyline | null>(null);
  const routeMarkersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const markerLibraryRef = useRef<typeof google.maps.marker.AdvancedMarkerElement | null>(null);
  const hasCenteredRef = useRef(false);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: getGoogleMapsApiKey(),
    libraries: GOOGLE_MAPS_LIBRARIES,
    version: "beta",
  });

  const {
    data: tracking,
    isLoading,
    isError,
  } = useGetPublicTrackingQuery(token ?? "", {
    skip: !token,
    pollingInterval: POLLING_INTERVAL_MS,
  });

  const assets = useMemo(() => tracking?.assets ?? [], [tracking?.assets]);

  const vehicles = useMemo(
    () => assets.filter((a) => a.assetType === ShareAssetType.Vehicle),
    [assets]
  );
  const trailers = useMemo(
    () => assets.filter((a) => a.assetType === ShareAssetType.Trailer),
    [assets]
  );

  const filteredVehicles = useMemo(
    () => vehicles.filter((a) => matchesSearch(a, search)),
    [vehicles, search]
  );
  const filteredTrailers = useMemo(
    () => trailers.filter((a) => matchesSearch(a, search)),
    [trailers, search]
  );

  const selectedAsset = useMemo(
    () => assets.find((asset) => getAssetKey(asset) === selectedAssetKey) ?? assets[0],
    [assets, selectedAssetKey]
  );

  const {
    data: routeHistory = [],
    isLoading: isRouteHistoryLoading,
    isFetching: isRouteHistoryFetching,
  } =
    useGetPublicVehicleHistoryQuery(
      { token: token ?? "", vehicleId: routeVehicleId ?? 0, daysBack: 3 },
      { skip: !token || !routeVehicleId || !showRoute }
    );
  const isRouteLoading = isRouteHistoryLoading || isRouteHistoryFetching;

  useEffect(() => {
    if (!selectedAssetKey && assets.length > 0) {
      const firstVehicle = vehicles.find((vehicle) => vehicle.vehicleId);
      const initialAsset = firstVehicle ?? assets[0];

      setSelectedAssetKey(getAssetKey(initialAsset));
      setRouteVehicleId(
        initialAsset.assetType === ShareAssetType.Vehicle && initialAsset.vehicleId
          ? initialAsset.vehicleId
          : null
      );
    }
  }, [assets, selectedAssetKey, vehicles]);

  const handleAssetClick = useCallback(
    (asset: PublicAssetLocation) => {
      const key = getAssetKey(asset);
      setSelectedAssetKey(key);
      setShowRoute(false);
      if (asset.assetType === ShareAssetType.Vehicle && asset.vehicleId) {
        setRouteVehicleId(asset.vehicleId);
      } else {
        setRouteVehicleId(null);
      }
    },
    []
  );

  const loadMarkerLibrary = useCallback(async () => {
    if (!markerLibraryRef.current) {
      const { AdvancedMarkerElement } = (await google.maps.importLibrary(
        "marker"
      )) as google.maps.MarkerLibrary;
      markerLibraryRef.current = AdvancedMarkerElement;
    }
    return markerLibraryRef.current;
  }, []);

  const clearMarkers = useCallback(() => {
    markersRef.current.forEach((marker) => { marker.map = null; });
    markersRef.current.clear();
  }, []);

  const clearRoute = useCallback(() => {
    if (routePolylineRef.current) {
      routePolylineRef.current.setMap(null);
      routePolylineRef.current = null;
    }
    routeMarkersRef.current.forEach((m) => { m.map = null; });
    routeMarkersRef.current = [];
  }, []);

  const fitAssets = useCallback(() => {
    if (!map || assets.length === 0) return;
    const bounds = new google.maps.LatLngBounds();
    let hasBounds = false;
    assets.forEach((asset) => {
      if (hasPosition(asset)) {
        bounds.extend({ lat: asset.latitude!, lng: asset.longitude! });
        hasBounds = true;
      }
    });
    if (hasBounds) map.fitBounds(bounds, 100);
  }, [assets, map]);

  useEffect(() => {
    if (!map || !isLoaded) return;
    let cancelled = false;

    const updateMarkers = async () => {
      const AdvancedMarkerElement = await loadMarkerLibrary();
      if (cancelled) return;
      clearMarkers();

      assets.filter(hasPosition).forEach((asset) => {
        const key = getAssetKey(asset);
        const isSelected = selectedAsset ? getAssetKey(selectedAsset) === key : false;
        const content =
          asset.assetType === ShareAssetType.Vehicle
            ? createPublicVehicleMarkerElement(asset, isDark, isSelected)
            : createTrailerMarkerElement(
                { deviceId: asset.deviceId ?? "", vehicleName: asset.name, deviceStatus: null },
                isSelected
              );

        const marker = new AdvancedMarkerElement({
          map,
          position: { lat: asset.latitude!, lng: asset.longitude! },
          content,
          zIndex: isSelected ? 20 : 10,
        });
        marker.addListener("click", () => handleAssetClick(asset));
        markersRef.current.set(key, marker);
      });

      if (!hasCenteredRef.current) {
        fitAssets();
        hasCenteredRef.current = true;
      }
    };

    updateMarkers();
    return () => { cancelled = true; };
  }, [assets, clearMarkers, fitAssets, handleAssetClick, isDark, isLoaded, loadMarkerLibrary, map, selectedAsset]);

  useEffect(() => {
    if (!map || !showRoute || selectedAsset?.assetType !== ShareAssetType.Vehicle) {
      clearRoute();
      return;
    }

    if (isRouteLoading) {
      clearRoute();
      return;
    }

    const validHistory = routeHistory.filter(
      (p) => p.latitude != null && p.longitude != null
    );
    if (validHistory.length < 2) {
      clearRoute();
      return;
    }

    const { path, pauses, startPosition } = processVehicleHistory(validHistory);

    clearRoute();

    if (path.length > 0) {
      routePolylineRef.current = createHistoryPolyline(path, map, isDark);
    }

    loadMarkerLibrary().then(() => {
      if (!map) return;

      if (startPosition) {
        routeMarkersRef.current.push(createStartMarker(startPosition, map));
      }

      pauses.forEach((pause, index) => {
        routeMarkersRef.current.push(
          createPauseMarker(pause, map, index, pauses.length, isDark)
        );
      });
    });
  }, [clearRoute, isDark, isRouteLoading, loadMarkerLibrary, map, routeHistory, selectedAsset, showRoute]);

  const centerOnSelectedAsset = useCallback(() => {
    if (!map || !selectedAsset || selectedAsset.latitude == null || selectedAsset.longitude == null) return;
    map.setCenter({ lat: selectedAsset.latitude, lng: selectedAsset.longitude });
    map.setZoom(ASSET_ZOOM);
  }, [map, selectedAsset]);

  const fitRouteBounds = useCallback(() => {
    if (!map || routeHistory.length === 0) return;
    const bounds = new google.maps.LatLngBounds();
    let hasPoints = false;
    routeHistory.forEach((p) => {
      if (p.latitude != null && p.longitude != null) {
        bounds.extend({ lat: p.latitude, lng: p.longitude });
        hasPoints = true;
      }
    });
    if (selectedAsset?.latitude != null && selectedAsset.longitude != null) {
      bounds.extend({ lat: selectedAsset.latitude, lng: selectedAsset.longitude! });
      hasPoints = true;
    }
    if (hasPoints) map.fitBounds(bounds, 80);
  }, [map, routeHistory, selectedAsset]);

  const onMapLoad = useCallback((mapInstance: google.maps.Map) => setMap(mapInstance), []);

  const onMapUnmount = useCallback(() => {
    clearMarkers();
    clearRoute();
    setMap(null);
    hasCenteredRef.current = false;
  }, [clearMarkers, clearRoute]);

  const mapOptions: google.maps.MapOptions = {
    mapId: process.env.REACT_APP_GOOGLE_MAPS_ID || "",
    disableDefaultUI: true,
    zoomControl: true,
    fullscreenControl: true,
    streetViewControl: false,
    mapTypeControl: false,
  };

  if (loadError || isError) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", p: 3 }}>
        <Card sx={{ maxWidth: 480 }}>
          <CardContent sx={{ textAlign: "center", p: 4 }}>
            <LinkOffIcon color="error" sx={{ fontSize: 44, mb: 1 }} />
            <Typography variant="h6" gutterBottom>
              Tracking link unavailable
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This link may have expired, been revoked, or the map could not be loaded.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (isLoading || !isLoaded) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", gap: 2 }}>
        <CircularProgress />
        <Typography color="text.secondary">Loading live tracking...</Typography>
      </Box>
    );
  }

  const expiresAt = tracking?.expiresAt ? new Date(tracking.expiresAt).toLocaleString() : null;
  const selectedKey = selectedAsset ? getAssetKey(selectedAsset) : undefined;

  const mapActionIconSx = {
    border: 1,
    borderColor: "primary.main",
    color: "primary.main",
    borderRadius: 1,
    width: 30,
    height: 30,
    flexShrink: 0,
    "& .MuiSvgIcon-root": { fontSize: "1rem" },
    "&:hover": {
      backgroundColor: "primary.light",
      color: "white",
      borderColor: "primary.dark",
    },
  } as const;

  const statusText =
    selectedAsset?.assetType === ShareAssetType.Trailer
      ? selectedAsset.drivingStatus === 1
        ? "Driving"
        : selectedAsset.drivingStatus === 0
          ? "Idle"
          : "ACC Off"
      : selectedAsset?.status || "Unknown";

  const showTwoColumns = vehicles.length > 0 && trailers.length > 0;

  return (
    <Box sx={{ height: "100vh", width: "100vw", position: "relative", overflow: "hidden" }}>
      <GoogleMap
        key={mapKey}
        mapContainerStyle={mapContainerStyle}
        center={
          selectedAsset?.latitude != null && selectedAsset.longitude != null
            ? { lat: selectedAsset.latitude, lng: selectedAsset.longitude }
            : DEFAULT_CENTER
        }
        zoom={selectedAsset?.latitude != null ? ASSET_ZOOM : DEFAULT_ZOOM}
        onLoad={onMapLoad}
        onUnmount={onMapUnmount}
        options={mapOptions}
      />

      <Fade in={isRouteLoading && showRoute} unmountOnExit>
        <Paper
          elevation={6}
          sx={{
            position: "absolute",
            bottom: 88,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1200,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 2,
            py: 1.25,
            borderRadius: 2,
            pointerEvents: "none",
          }}
        >
          <Box sx={{ position: "relative", display: "inline-flex" }}>
            <CircularProgress size={40} thickness={4} />
            <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <RouteIcon sx={{ fontSize: "1.35rem", color: "primary.main" }} />
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary" fontWeight="medium">
            Loading route…
          </Typography>
        </Paper>
      </Fade>

      <Card
        sx={{
          position: "absolute",
          top: 16,
          left: 16,
          width: {
            xs: "calc(100vw - 32px)",
            sm: isPanelExpanded ? (showTwoColumns ? 480 : 328) : 328,
          },
          maxWidth: "calc(100vw - 32px)",
          maxHeight: "calc(100vh - 32px)",
          overflow: "auto",
          zIndex: 2,
          borderRadius: 2,
          boxShadow: 4,
          transition: muiTheme.transitions.create("width", {
            duration: muiTheme.transitions.duration.shorter,
          }),
        }}
      >
        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
          {/* Header */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
            <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 700 }}>
              Live Tracking
            </Typography>
            <Tooltip title={mode === "dark" ? "Light mode" : "Dark mode"}>
              <IconButton
                size="small"
                onClick={() => {
                  toggleTheme();
                  setMapKey((k) => k + 1);
                }}
              >
                {mode === "dark" ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title={isPanelExpanded ? "Collapse asset list" : "Expand asset list"}>
              <IconButton
                size="small"
                onClick={() => setIsPanelExpanded((expanded) => !expanded)}
                aria-label={isPanelExpanded ? "Collapse asset list" : "Expand asset list"}
              >
                {isPanelExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Tooltip>
          </Box>

          <Collapse in={isPanelExpanded} timeout="auto" unmountOnExit>
            <Box>
              {expiresAt && (
                <Typography variant="caption" color="text.secondary">
                  Link expires {expiresAt}
                </Typography>
              )}

              {/* Search */}
              {assets.length > 0 && (
                <TextField
                  size="small"
                  placeholder="Search assets…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  fullWidth
                  sx={{ mt: 1.25 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: search ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearch("")} edge="end">
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              )}

              {/* Asset lists */}
              <Box
                sx={{
                  mt: 1.25,
                  display: "grid",
                  gridTemplateColumns: showTwoColumns ? { xs: "1fr", sm: "1fr 1fr" } : "1fr",
                  gap: 1,
                  alignItems: "start",
                }}
              >
                {/* Vehicles column */}
                {vehicles.length > 0 && (
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                      <TruckIcon fontSize="small" color="action" />
                      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                        Vehicles
                      </Typography>
                      <Chip
                        label={filteredVehicles.length === vehicles.length ? vehicles.length : `${filteredVehicles.length}/${vehicles.length}`}
                        size="small"
                        sx={{ height: 18, fontSize: "0.65rem", ml: "auto" }}
                      />
                    </Box>
                    <Box
                      sx={{
                        maxHeight: 190,
                        overflowY: "auto",
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 0.5,
                      }}
                    >
                      <AssetList
                        assets={filteredVehicles}
                        selectedKey={selectedKey}
                        onSelect={handleAssetClick}
                        emptyLabel="No vehicles match"
                      />
                    </Box>
                  </Box>
                )}

                {/* Trailers column */}
                {trailers.length > 0 && (
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                      <TrailerIcon fontSize="small" color="action" />
                      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                        Trailers
                      </Typography>
                      <Chip
                        label={filteredTrailers.length === trailers.length ? trailers.length : `${filteredTrailers.length}/${trailers.length}`}
                        size="small"
                        sx={{ height: 18, fontSize: "0.65rem", ml: "auto" }}
                      />
                    </Box>
                    <Box
                      sx={{
                        maxHeight: 190,
                        overflowY: "auto",
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 0.5,
                      }}
                    >
                      <AssetList
                        assets={filteredTrailers}
                        selectedKey={selectedKey}
                        onSelect={handleAssetClick}
                        emptyLabel="No trailers match"
                      />
                    </Box>
                  </Box>
                )}
              </Box>

              {assets.length === 0 && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
                  <ErrorOutlineIcon color="warning" />
                  <Typography variant="body2" color="text.secondary">
                    No assets are available on this tracking link.
                  </Typography>
                </Box>
              )}
            </Box>
          </Collapse>

          <Divider sx={{ my: 1.5 }} />

          {/* Selected asset detail */}
          {selectedAsset ? (
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, flex: 1 }}>
                  {selectedAsset.assetType === ShareAssetType.Vehicle
                    ? <TruckIcon fontSize="small" color="action" />
                    : <TrailerIcon fontSize="small" color="action" />}
                  <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700 }}>
                    {selectedAsset.name}
                  </Typography>
                </Box>
                <Chip size="small" label={statusText} color={statusText === "Active" ? "success" : "default"} />
              </Box>

              {selectedAsset.speed != null && (
                <Box sx={{ mb: 0.75 }}>
                  <Chip
                    size="small"
                    icon={<SpeedIcon />}
                    label={`${Math.round(selectedAsset.speed * 0.621371)} mph`}
                  />
                </Box>
              )}

              {selectedAsset.address && (
                <Typography variant="body2" noWrap color="text.secondary" sx={{ mb: 0.75 }}>
                  {selectedAsset.address}
                </Typography>
              )}

              <Stack direction="row" spacing={1.25} alignItems="flex-start" flexWrap="wrap">
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "block",
                      fontSize: "0.6rem",
                      lineHeight: 1,
                      mb: 0.375,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Asset View
                  </Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Tooltip title="Center on selected asset">
                      <span>
                        <IconButton
                          size="small"
                          onClick={centerOnSelectedAsset}
                          disabled={!hasPosition(selectedAsset)}
                          aria-label="Center on selected asset"
                          sx={mapActionIconSx}
                        >
                          <MyLocationIcon />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Fit all shared assets">
                      <IconButton
                        size="small"
                        onClick={fitAssets}
                        aria-label="Fit all shared assets"
                        sx={mapActionIconSx}
                      >
                        <ZoomOutMapIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>

                {selectedAsset.assetType === ShareAssetType.Vehicle && (
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        fontSize: "0.6rem",
                        lineHeight: 1,
                        mb: 0.375,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Route
                    </Typography>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Tooltip title={isRouteLoading ? "Loading route…" : "Show or hide vehicle route"}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.25,
                            px: 0.375,
                            py: 0.125,
                            borderRadius: 1,
                            border: 1,
                            borderColor: "primary.main",
                            bgcolor: showRoute && !isRouteLoading ? "primary.light" : "transparent",
                            flexShrink: 0,
                            "&:hover": {
                              bgcolor: showRoute && !isRouteLoading ? "primary.main" : "primary.light",
                            },
                          }}
                        >
                          {isRouteLoading ? (
                            <CircularProgress size={14} sx={{ color: "primary.main", mx: 0.25 }} />
                          ) : (
                            <RouteIcon sx={{ fontSize: "1rem", color: showRoute ? "white" : "primary.main" }} />
                          )}
                          <Switch
                            size="small"
                            checked={showRoute}
                            disabled={isRouteLoading}
                            onChange={() => setShowRoute((v) => !v)}
                            inputProps={{ "aria-label": "Show vehicle route" }}
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-checked": {
                                color: "white",
                                "& + .MuiSwitch-track": { backgroundColor: "white" },
                              },
                              "& .MuiSwitch-switchBase": {
                                color: "primary.main",
                                "& + .MuiSwitch-track": { backgroundColor: "primary.main" },
                              },
                            }}
                          />
                        </Box>
                      </Tooltip>

                      {showRoute && routeHistory.length > 0 && (
                        <Tooltip title="Fit selected route">
                          <IconButton
                            size="small"
                            onClick={fitRouteBounds}
                            aria-label="Fit selected route"
                            sx={mapActionIconSx}
                          >
                            <ZoomOutMapIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </Box>
          ) : null}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PublicTrackingPage;
